import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import AlertBanner from "../components/AlertBanner";
import FeaturedCard from "../components/FeaturedCard";
import AppTile from "../components/AppTile";
import SupportPanel from "../components/SupportPanel";
import NewsPanel from "../components/NewsPanel";
import { Button } from "../components/ui/button";
import { List, Rows3, Pencil, Globe, Lock, ChevronsUpDown, Sparkles, Check, ChevronUp, ChevronDown, X, Megaphone, Gauge } from "lucide-react";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import UCVigilStrip from "../components/UCVigilStrip";
import UptimeHistoryCard from "../components/UptimeHistoryCard";
import CollapsibleSection from "../components/CollapsibleSection";
import { useSettings } from "../context/SettingsContext";

const DASH_STORAGE_PREFIX = "ucv:dashboard:collapsed:";

export default function Dashboard() {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const { settings } = useSettings();
  const showQuickLaunch = settings?.show_quick_launch !== false; // default true when unset
  const showFeaturedGlobal = settings?.show_featured_article !== false;
  const featuredRevealedAt = settings?.featured_revealed_at || null;
  const isSuperAdmin = currentUser?.role === "Super Admin";
  const [data, setData] = useState(null);
  const [view, setView] = useState("compact"); // list | compact
  const [category, setCategory] = useState("all");
  const [editMode, setEditMode] = useState(false);
  const [orderedApps, setOrderedApps] = useState(null); // used while editing
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (editMode && data) setOrderedApps([...data.apps]);
    if (!editMode) setOrderedApps(null);
  }, [editMode, data]);

  const filteredApps = useMemo(() => {
    if (!data) return [];
    const list = editMode && orderedApps ? orderedApps : data.apps;
    if (category === "all") return list;
    return list.filter((a) => a.category === category);
  }, [data, category, editMode, orderedApps]);

  const moveApp = (id, dir) => {
    if (!orderedApps) return;
    const idx = orderedApps.findIndex((a) => a.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= orderedApps.length) return;
    const next = [...orderedApps];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrderedApps(next);
  };

  const saveOrder = async () => {
    if (!orderedApps) { setEditMode(false); return; }
    setSavingOrder(true);
    try {
      const payload = orderedApps.map((a, i) => ({ id: a.id, display_order: (i + 1) * 10 }));
      await api.post("/admin/apps/reorder", payload);
      const { data: fresh } = await api.get("/dashboard");
      setData(fresh);
      setEditMode(false);
      toast({ title: "App order saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSavingOrder(false); }
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-24 rounded-xl bg-muted/50 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-muted/40 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const { user, alerts, featured_article, support_contacts, news, escalation_intro } = data;

  return (
    <div className="space-y-6">
      {/* Welcome row */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight">Welcome, {user?.name}</h1>
          <p className="text-[13px] text-muted-foreground">Overview</p>
        </div>
        <span className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 text-primary text-[11.5px] font-semibold px-2.5 py-1">
          {user?.role}
        </span>
      </div>

      {/* Alerts */}
      {alerts?.map((a) => (
        <AlertBanner
          key={a.id}
          alert={a}
          onChanged={() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {})}
        />
      ))}

      {/* Left 2/3 = collapsible sections (Featured, UC Vigil) + always-open Uptime.
          Right 1/3 = Support pinned above Quick Launch, then News. */}
      {(() => {
        const featuredVisible = showFeaturedGlobal && !!featured_article;

        // Right-rail composition — Support Panel is always pinned above Quick Launch.
        const rightRail = (
          <aside className="space-y-4" data-testid="dashboard-right-rail">
            <SupportPanel contacts={support_contacts} intro={escalation_intro} />
            {showQuickLaunch && (
              <section className="rounded-xl border border-border bg-card p-3" data-testid="quick-launch-panel">
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-primary" />
                    <h3 className="text-[13.5px] font-semibold">Quick launch</h3>
                    <span className="text-[11px] text-muted-foreground">{data.apps.length}</span>
                  </div>
                  <Link to="/apps" className="text-[12px] text-primary hover:underline">View all</Link>
                </div>
                <div className="grid grid-cols-3 gap-2 auto-rows-min">
                  {data.apps.map((app) => <AppTile key={app.id} app={app} variant="compact" />)}
                </div>
              </section>
            )}
          </aside>
        );

        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2 space-y-4">
              {/* Featured announcement — collapsible; admin re-toggling ON bumps
                  settings.featured_revealed_at which force-reopens for every user. */}
              {featuredVisible && (
                <CollapsibleSection
                  id={`announcement:${featured_article.id}`}
                  storagePrefix={DASH_STORAGE_PREFIX}
                  title={featured_article.title}
                  description={featured_article.label || "Announcement"}
                  icon={Megaphone}
                  defaultOpen={true}
                  revealAt={featuredRevealedAt}
                  dense
                >
                  <FeaturedCard article={featured_article} />
                </CollapsibleSection>
              )}

              {/* UC Vigil aggregated stats — always rendered, collapsible. */}
              <CollapsibleSection
                id="uc-vigil"
                storagePrefix={DASH_STORAGE_PREFIX}
                title="UC Vigil"
                description="Aggregated compliance & health · 30 days"
                icon={Gauge}
                defaultOpen={true}
                dense
              >
                <UCVigilStrip />
              </CollapsibleSection>

              {/* Uptime — always visible, never collapsible (per admin preference). */}
              <UptimeHistoryCard />
            </div>

            {/* Right column — Support pinned on top, then Quick Launch, then News */}
            <div className="space-y-6">
              {rightRail}
              <NewsPanel items={news} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
